/**
 * /equipment — Equipment register (Phase 1 of CONSTRUCTION-EQUIPMENT-COSTING-001).
 *
 * Lists all equipment for the current entity with category / ownership /
 * search filters and a "show inactive" toggle. Click a row → detail page;
 * row's "…" menu offers Edit + Deactivate.
 */
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  MenuItem,
  Menu,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  DataGridPro,
  type GridColDef,
  type GridRenderCellParams,
} from "@mui/x-data-grid-pro";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/lib/api";
import {
  equipmentItemKeys,
  equipmentItemsQueryOptions,
} from "@/lib/queries";
import type { EquipmentItem } from "@/lib/types";
import { gridDefaults, FlexGrid } from "@/grids/gridDefaults";
import { HelpHint } from "@/components/HelpHint";
import { EquipmentFormDialog } from "@/components/EquipmentFormDialog";
import { useConstructionPack } from "@/lib/useConstructionPack";
import { useToast } from "@/components/Toaster";

export const Route = createFileRoute("/_auth/equipment/")({
  component: EquipmentRegisterPage,
});

function fmtMoney(v: string | null | undefined) {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(n);
}

function EquipmentRegisterPage() {
  const { enabled, loading: packLoading } = useConstructionPack();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();

  const [search, setSearch] = useState("");
  const [ownership, setOwnership] = useState<string>("");
  const [category, setCategory] = useState<string>("");
  const [showInactive, setShowInactive] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const filters = useMemo(
    () => ({
      search: search || undefined,
      ownership_type: ownership || undefined,
      category: category || undefined,
      active_only: !showInactive,
    }),
    [search, ownership, category, showInactive],
  );

  const listQ = useQuery(equipmentItemsQueryOptions(filters));
  const items: EquipmentItem[] = listQ.data ?? [];

  const categories = useMemo(() => {
    const s = new Set<string>();
    for (const it of items) if (it.category) s.add(it.category);
    return Array.from(s).sort();
  }, [items]);

  const deactivateMut = useMutation({
    mutationFn: (id: string) =>
      api(`/api/construction/equipment/${id}`, {
        method: "PATCH",
        body: { is_active: false },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: equipmentItemKeys.all });
      toast.success("Equipment deactivated");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (!packLoading && !enabled) return null;

  const columns: GridColDef<EquipmentItem>[] = [
    {
      field: "code",
      headerName: "Code",
      width: 130,
      renderCell: (p: GridRenderCellParams<EquipmentItem>) => (
        <Link
          to="/equipment/$equipmentId"
          params={{ equipmentId: p.row.id }}
          style={{
            fontFamily: "monospace",
            fontWeight: 600,
            color: "inherit",
            textDecoration: "none",
          }}
        >
          {p.value as string}
        </Link>
      ),
    },
    { field: "name", headerName: "Name", flex: 1, minWidth: 200 },
    { field: "category", headerName: "Category", width: 140 },
    {
      field: "ownership_type",
      headerName: "Ownership",
      width: 120,
      renderCell: (p) => (
        <Chip
          size="small"
          label={String(p.value)}
          color={
            p.value === "owned"
              ? "primary"
              : p.value === "rented"
                ? "default"
                : "info"
          }
          variant="outlined"
        />
      ),
    },
    {
      field: "fixed_asset_id",
      headerName: "Linked asset",
      width: 110,
      renderCell: (p) =>
        p.value ? (
          <Link
            to="/assets"
            style={{ color: "inherit", textDecoration: "underline" }}
          >
            View
          </Link>
        ) : (
          <span style={{ color: "var(--mui-palette-text-disabled)" }}>—</span>
        ),
    },
    {
      field: "run_rate",
      headerName: "Run rate",
      width: 110,
      align: "right",
      headerAlign: "right",
      valueGetter: (_v, row: EquipmentItem) => {
        if (!row.ownership_rate && !row.operating_rate_est) return null;
        const own = Number(row.ownership_rate ?? 0);
        const op = Number(row.operating_rate_est ?? 0);
        return own + op;
      },
      renderCell: (p) =>
        fmtMoney(p.value != null ? String(p.value) : null) +
        (p.value != null ? "/hr" : ""),
    },
    {
      field: "is_active",
      headerName: "Active",
      width: 90,
      renderCell: (p) =>
        p.value ? (
          <Chip
            size="small"
            color="success"
            label="Active"
            variant="outlined"
          />
        ) : (
          <Chip
            size="small"
            color="default"
            label="Inactive"
            variant="outlined"
          />
        ),
    },
    {
      field: "_actions",
      headerName: "",
      width: 60,
      sortable: false,
      filterable: false,
      renderCell: (p) => (
        <RowActions
          item={p.row}
          onEdit={() =>
            navigate({
              to: "/equipment/$equipmentId",
              params: { equipmentId: p.row.id },
            })
          }
          onDeactivate={() => deactivateMut.mutate(p.row.id)}
        />
      ),
    },
  ];

  return (
    <Box
      sx={{
        p: 3,
        height: "calc(100vh - 56px)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="h5" fontWeight={600}>
            Equipment
          </Typography>
          <HelpHint
            docPath="equipment-costing/README.md"
            label="Equipment costing help"
          />
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            component={Link}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            to={"/equipment/cost-posting" as any}
          >
            Cost Posting
          </Button>
          <Button
            variant="outlined"
            component={Link}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            to={"/equipment/cost-postings" as any}
          >
            Past postings
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
          >
            New equipment
          </Button>
        </Stack>
      </Stack>

      <Stack direction="row" spacing={1.5} mb={2} alignItems="center">
        <TextField
          size="small"
          label="Search code / name / make / model"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 320 }}
        />
        <Select
          size="small"
          displayEmpty
          value={ownership}
          onChange={(e) => setOwnership(e.target.value)}
          sx={{ minWidth: 140 }}
        >
          <MenuItem value="">All ownership</MenuItem>
          <MenuItem value="owned">Owned</MenuItem>
          <MenuItem value="rented">Rented</MenuItem>
          <MenuItem value="leased">Leased</MenuItem>
        </Select>
        <Select
          size="small"
          displayEmpty
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="">All categories</MenuItem>
          {categories.map((c) => (
            <MenuItem key={c} value={c}>
              {c}
            </MenuItem>
          ))}
        </Select>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={showInactive}
              onChange={(_, v) => setShowInactive(v)}
            />
          }
          label="Show inactive"
        />
      </Stack>

      {listQ.isLoading && <CircularProgress size={20} />}
      {listQ.error && (
        <Alert severity="error">Failed to load equipment.</Alert>
      )}

      <Box sx={{ flex: 1, minHeight: 0 }}>
        <FlexGrid>
          <DataGridPro
            {...gridDefaults}
            rows={items}
            columns={columns}
            getRowId={(r) => r.id}
            onRowClick={(p) =>
              navigate({
                to: "/equipment/$equipmentId",
                params: { equipmentId: String(p.id) },
              })
            }
            disableRowSelectionOnClick
          />
        </FlexGrid>
      </Box>

      <EquipmentFormDialog
        open={createOpen}
        mode="create"
        onClose={() => setCreateOpen(false)}
      />
    </Box>
  );
}

function RowActions({
  item,
  onEdit,
  onDeactivate,
}: {
  item: EquipmentItem;
  onEdit: () => void;
  onDeactivate: () => void;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  return (
    <>
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          setAnchor(e.currentTarget);
        }}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem
          onClick={() => {
            setAnchor(null);
            onEdit();
          }}
        >
          Edit
        </MenuItem>
        {item.is_active && (
          <MenuItem
            onClick={() => {
              setAnchor(null);
              onDeactivate();
            }}
          >
            Deactivate
          </MenuItem>
        )}
      </Menu>
    </>
  );
}
